import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Boxes, PlusCircle, FolderTree, MessagesSquare, LogOut, Cpu, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const item = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
    isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
  }`;

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 grid grid-cols-1 lg:grid-cols-[260px_1fr]">
      <aside data-testid="admin-sidebar" className="hidden lg:flex lg:flex-col border-r border-slate-200 bg-white">
        <div className="h-16 px-5 border-b border-slate-200 flex items-center gap-2.5">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-slate-900 text-white">
            <Cpu className="w-5 h-5" strokeWidth={2.2} />
          </span>
          <div>
            <div className="font-display font-bold text-sm leading-tight">Mymi Admin</div>
            <div className="text-[11px] text-slate-500 leading-tight">Inventory & Orders</div>
          </div>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          <NavLink to="/admin" end className={item} data-testid="admin-nav-dashboard">
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </NavLink>
          <NavLink to="/admin/inventory" className={item} data-testid="admin-nav-inventory">
            <Boxes className="w-4 h-4" /> Inventory
          </NavLink>
          <NavLink to="/admin/products/new" className={item} data-testid="admin-nav-add-product">
            <PlusCircle className="w-4 h-4" /> Add Product
          </NavLink>
          <NavLink to="/admin/categories" className={item} data-testid="admin-nav-categories">
            <FolderTree className="w-4 h-4" /> Categories
          </NavLink>
          <NavLink to="/admin/inquiries" className={item} data-testid="admin-nav-inquiries">
            <MessagesSquare className="w-4 h-4" /> Inquiries
          </NavLink>
        </nav>
        <div className="p-3 border-t border-slate-200 space-y-2">
          <Link to="/" target="_blank" className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500 hover:text-slate-900" data-testid="admin-view-site">
            <ExternalLink className="w-3.5 h-3.5" /> View public site
          </Link>
          <div className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Signed in as</div>
            <div className="text-sm font-medium text-slate-900 truncate">{user?.email}</div>
          </div>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-rose-600 hover:bg-rose-50 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>
      <main className="min-h-screen">
        <div className="lg:hidden h-14 border-b border-slate-200 bg-white px-4 flex items-center justify-between">
          <Link to="/admin" className="font-display font-bold">Mymi Admin</Link>
          <button onClick={handleLogout} className="text-sm text-rose-600" data-testid="admin-logout-mobile">Log out</button>
        </div>
        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
